/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime, FetchFn} from './AdobeMetricsRuntime';
import createPayload, {Payload} from './Payload';
import {Level, RecordType} from '@exc/metrics';
import {LOGGER_NAME} from './MetricsRuntime';

export interface TransportConfig {
  /**
   * Ingestion API key.
   */
  apiKey?: string;
}

const DEFAULT_FLUSH_PERIOD = 1000;
const INIT_IMMEDIATE_BACKOFF_FLUSH_PERIOD = 3000;

export default class Transport {
  public static devServer = 'https://telemetry-dev.adobe.io';
  // public static devServer = 'http://localhost:8080';
  private ingestURL = Transport.devServer + '/ingest';
  private defaultFlushPeriod: number;
  private flushPeriod = 1000;
  private batchMode = true;
  private lastBatch?: number;
  private maxPayloadSize = 500000;
  private readonly clearFlushTimeout: () => void;
  private readonly setFlushTimeout: () => void;
  public transmit = true;
  private apiKey = 'metrics-sdk-js';
  private fetchFn?: FetchFn;
  public readonly payload: Payload;
  private adobeMetrics: AdobeMetricsRuntime;

  /**
   * Initialize the Transport layer. Currently, this just initializes
   * the base url. The reason for an explicit init() is to definitively
   * control ordering and exception handling during startup.
   *
   * @param {object} config A configuration object for the desired state.
   */
  public readonly init: (config?: TransportConfig) => void;

  public constructor(adobeMetrics: AdobeMetricsRuntime, global: Window & typeof globalThis) {
    this.adobeMetrics = adobeMetrics;
    const getFetch = (): FetchFn => global._originalFetch?.bind(global) as FetchFn;
    const {clearTimeout, setTimeout} = global;
    let flushTimeoutId: any;

    this.fetchFn = getFetch();

    this.setFlushTimeout = (): void => {
      flushTimeoutId = setTimeout(async (): Promise<number | void> => {
        try {
          return await this.flush('timer');
        } catch (e) {
          // avoid throwing an uncaught exception here
          return Promise.resolve();
        }
      }, this.flushPeriod);
    };

    this.clearFlushTimeout = (): void => {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = undefined;
    };

    this.defaultFlushPeriod = adobeMetrics?.runtime?.options?.transport?.flushPeriod || DEFAULT_FLUSH_PERIOD;

    this.payload = createPayload(adobeMetrics, global);
    this.init = (config?: TransportConfig): void => {
      this.apiKey = config?.apiKey || this.apiKey;
      this.fetchFn = getFetch();
      const transport = adobeMetrics.runtime.options.transport;
      switch (adobeMetrics.metricsState.environment) {
        case 'stage':
          this.ingestURL = `https://telemetry-stage.adobe.io`;
          break;
        case 'prod':
          this.ingestURL = 'https://telemetry.adobe.io';
          break;
        default:
          this.ingestURL = Transport.devServer;
      }
      this.ingestURL += '/ingest';
      this.batchMode = transport.batch;
      this.transmit = transport.transmit;
      this.defaultFlushPeriod = adobeMetrics.runtime.options.transport.flushPeriod;
      this.maxPayloadSize = transport.maxPayloadSize;
    };
  }

  /**
   * HTTP POST the current payload to the metrics gateway for ingestion
   * into Azure Data Explorer. The object fields need to correspond to the
   * mapped table schema in ADE.
   *
   * @param {string} body - The payload body to send.
   * @returns {Promise<Response|undefined>} A promise for the response.
   */
  public post(body: string): Promise<Response | undefined> {
    if (!this.fetchFn) {
      return Promise.resolve(undefined);
    }
    try {
      return this.fetchFn(this.ingestURL + '?api_key=' + this.apiKey, {
        body,
        headers: {
          'Content-Type': 'text/plain;type=entry;charset=utf-8',
        },
        method: 'POST',
        priority: 'low',
      })
        .then((response: Response) => {
          if (response && response.ok) {
            this.flushPeriod = this.defaultFlushPeriod;
            return Promise.resolve(response);
          } else {
            return Promise.reject(new Error(response.statusText));
          }
        })
        .catch((err) => {
          this.adobeMetrics.log(RecordType.LOG, Level.ERROR, LOGGER_NAME, undefined, `PayloadPostFailed`, {
            error: err.message,
            stack: err.stack ? err.stack : '',
          });
          if (this.flushPeriod === 0) {
            this.flushPeriod = INIT_IMMEDIATE_BACKOFF_FLUSH_PERIOD;
          } else {
            this.flushPeriod *= 2;
          }

          // never return Promise.reject. Swallow and log.
          /* eslint-disable no-console */
          console.log('Error in fetch.catch, ', err);
          /* eslint-enable no-console */
          const fakeResponse: Response = new Response(null);
          return Promise.resolve(fakeResponse);
        });
    } catch (err: any) {
      this.adobeMetrics.log(RecordType.LOG, Level.ERROR, LOGGER_NAME, undefined, `PayloadPostFailed`, {
        error: err.message,
        stack: err.stack ? err.stack : '',
      });
      if (this.flushPeriod === 0) {
        this.flushPeriod = INIT_IMMEDIATE_BACKOFF_FLUSH_PERIOD;
      } else {
        this.flushPeriod *= 2;
      }

      // never return Promise.reject. Swallow and log.
      /* eslint-disable no-console */
      console.log('Error in fetchFn', err);
      /* eslint-enable no-console */
      const fakeResponse: Response = new Response(null);
      return Promise.resolve(fakeResponse);
    }
  }

  /**
   * Send a payload to the metrics gateway for ingestion into Azure Data
   * explorer. The object fields need to correspond to the mapped table schema
   * in ADX.
   *
   * @param {string} flush A string indicating the reason the payload should be
   * immediately flushed.
   * @returns {object} A promise that resolves to the number of records sent,
   * which may be zero if the payload was empty or transmit is false.
   */
  public async send(flush: string | undefined = undefined): Promise<number> {
    if (!this.transmit) {
      return Promise.resolve(0);
    }

    // make note of the reason to flush
    if (
      flush ||
      (!this.batchMode && (flush = 'immediate')) ||
      (this.payload.size() > this.maxPayloadSize && (flush = 'maxSize'))
    ) {
      return this.flush(flush);
    }

    const now = Date.now();
    if (!this.lastBatch || now - this.lastBatch >= this.flushPeriod) {
      this.lastBatch = now;
      this.setFlushTimeout();
    }
    return Promise.resolve(0);
  }

  /*
   * Flush non-empty payloads immediately.
   */
  public async flush(reason: string): Promise<number> {
    // re-init in case runtime values changed
    // flushPeriod would be picked up for the next loop
    this.init();
    this.clearFlushTimeout();

    const size = this.payload.count();
    if (size > 0) {
      this.payload.appendPayloadCount(reason);
      const body = this.payload.toJSONString();
      this.lastBatch = undefined;
      this.payload.next();
      if (this.fetchFn) {
        return this.post(body).then(() => Promise.resolve(size));
      }
    }
    return Promise.resolve(0);
  }
}
